<template>
  <div class="journey-mini-preview rounded-xl overflow-hidden" :style="{ background: bgGradient }">
    <div
      class="flex items-start px-6 py-5"
      :class="vertical ? 'flex-col items-center justify-center gap-2' : 'justify-between'"
    >
      <template v-for="(lesson, i) in previewLessons" :key="lesson.id">
        <!-- Connector line between nodes -->
        <div
          v-if="i > 0"
          class="rounded-full shrink-0"
          :class="vertical ? 'w-0.5 h-4' : 'h-0.5 flex-1 mx-1'"
          :style="{ backgroundColor: connectorColor(i), marginTop: vertical ? '' : connectorTop }"
        />

        <!-- Themed node with avatar on current -->
        <div class="shrink-0 flex flex-col items-center">
          <!-- Theme avatar above current node -->
          <div v-if="lesson.status === 'current'" class="mb-1">
            <!-- Hiker for trail / standard -->
            <div v-if="theme === 'mountain-trail' || theme === 'standard'" class="avatar-bounce">
              <svg width="28" height="34" viewBox="0 0 36 44" fill="none" class="drop-shadow-md">
                <circle cx="18" cy="10" r="8" fill="#fbbf24" />
                <circle cx="14" cy="8" r="1.5" fill="#1e293b" />
                <circle cx="22" cy="8" r="1.5" fill="#1e293b" />
                <path d="M15 13 Q18 16 21 13" stroke="#1e293b" stroke-width="1.2" fill="none" />
                <ellipse cx="18" cy="4" rx="9" ry="2.5" fill="#16a34a" />
                <rect x="12" y="1" width="12" height="4" rx="2" fill="#16a34a" />
                <rect x="12" y="18" width="12" height="12" rx="3" fill="#3b82f6" />
                <rect x="24" y="18" width="6" height="10" rx="2" fill="#ef4444" />
                <rect x="13" y="30" width="4" height="10" rx="2" fill="#64748b" />
                <rect x="19" y="30" width="4" height="10" rx="2" fill="#64748b" />
                <rect x="12" y="38" width="6" height="4" rx="2" fill="#92400e" />
                <rect x="18" y="38" width="6" height="4" rx="2" fill="#92400e" />
                <line x1="8" y1="16" x2="6" y2="40" stroke="#a16207" stroke-width="2" stroke-linecap="round" />
              </svg>
            </div>
            <!-- Car for road -->
            <div v-else-if="theme === 'road-map'" class="car-bounce">
              <svg width="36" height="22" viewBox="0 0 48 30" fill="none" class="drop-shadow-md">
                <rect x="4" y="10" width="40" height="14" rx="4" fill="#ef4444" />
                <path d="M14,10 L18,2 L34,2 L38,10" fill="#dc2626" />
                <path d="M19,3 L17,9 L25,9 L25,3 Z" fill="#bfdbfe" opacity="0.8" />
                <path d="M27,3 L27,9 L35,9 L33,3 Z" fill="#bfdbfe" opacity="0.8" />
                <rect x="1" y="14" width="6" height="6" rx="2" fill="#b91c1c" />
                <rect x="41" y="14" width="6" height="6" rx="2" fill="#b91c1c" />
                <circle cx="4" cy="16" r="2" fill="#fbbf24" />
                <circle cx="44" cy="16" r="2" fill="#fca5a5" />
                <circle cx="14" cy="24" r="5" fill="#1e293b" />
                <circle cx="14" cy="24" r="2.5" fill="#64748b" />
                <circle cx="36" cy="24" r="5" fill="#1e293b" />
                <circle cx="36" cy="24" r="2.5" fill="#64748b" />
              </svg>
            </div>
            <!-- Rocket for space -->
            <div v-else-if="theme === 'space-mission'" class="rocket-float">
              <svg width="28" height="40" viewBox="0 0 40 56" fill="none" class="drop-shadow-md">
                <ellipse cx="20" cy="52" rx="5" ry="6" fill="#f97316" opacity="0.8" class="flame-flicker" />
                <ellipse cx="20" cy="50" rx="3" ry="4" fill="#fbbf24" opacity="0.9" class="flame-flicker" />
                <path d="M14,42 L14,20 Q14,8 20,2 Q26,8 26,20 L26,42 Z" fill="#e2e8f0" />
                <path d="M17,18 Q17,10 20,4 Q23,10 23,18 Z" fill="#ef4444" />
                <circle cx="20" cy="26" r="4" fill="#38bdf8" />
                <circle cx="19" cy="25" r="1.5" fill="white" opacity="0.5" />
                <path d="M14,36 L8,44 L14,42 Z" fill="#ef4444" />
                <path d="M26,36 L32,44 L26,42 Z" fill="#ef4444" />
                <rect x="14" y="34" width="12" height="2" fill="#94a3b8" rx="1" />
              </svg>
            </div>
          </div>

          <component
            :is="nodeComponent"
            :lesson="lesson"
            :index="lesson._originalIndex"
            v-bind="nodeComponent === RoadMapNode ? { side: 'right' } : {}"
            @select="$emit('select', $event)"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import TrailNode from '@/components/trail/TrailNode.vue';
import RoadMapNode from '@/components/roadmap/RoadMapNode.vue';
import SpaceNode from '@/components/space/SpaceNode.vue';

const props = defineProps({
  units: { type: Array, required: true },
  theme: { type: String, default: 'standard' },
  vertical: { type: Boolean, default: false }
});

defineEmits(['select']);

const flatLessons = computed(() => {
  return props.units.flatMap(u => u.lessons);
});

const previewLessons = computed(() => {
  const all = flatLessons.value;
  if (!all.length) return [];

  const currentIdx = all.findIndex(l => l.status === 'current');

  let start, end;
  if (currentIdx === -1) {
    start = Math.max(0, all.length - 3);
    end = all.length;
  } else if (currentIdx === 0) {
    start = 0;
    end = Math.min(3, all.length);
  } else {
    start = currentIdx - 1;
    end = Math.min(currentIdx + 2, all.length);
  }

  return all.slice(start, end).map(l => ({
    ...l,
    _originalIndex: all.indexOf(l)
  }));
});

const nodeComponent = computed(() => {
  switch (props.theme) {
    case 'mountain-trail': return TrailNode;
    case 'road-map': return RoadMapNode;
    case 'space-mission': return SpaceNode;
    default: return TrailNode;
  }
});

const bgGradient = computed(() => {
  switch (props.theme) {
    case 'mountain-trail': return 'linear-gradient(135deg, #e0f2fe, #dcfce7)';
    case 'road-map': return 'linear-gradient(135deg, #dcfce7, #d9f99d)';
    case 'space-mission': return 'linear-gradient(135deg, #020617, #1e1b4b)';
    default: return 'transparent';
  }
});

// Vertical offset to center connectors on the node circle (accounting for avatar above current)
const connectorTop = computed(() => {
  switch (props.theme) {
    case 'space-mission': return '30px';
    case 'road-map': return '18px';
    default: return '26px';
  }
});

function connectorColor(i) {
  const prev = previewLessons.value[i - 1];
  const curr = previewLessons.value[i];
  if (prev?.status === 'skipped' || curr?.status === 'skipped') return '#f97316'; // orange — skipped gap
  if (prev?.status === 'completed') return '#22c55e';
  if (props.theme === 'space-mission') return '#334155';
  return '#d1d5db';
}
</script>

<style scoped>
.avatar-bounce {
  animation: mini-bounce 2s ease-in-out infinite;
}
.car-bounce {
  animation: mini-drive 1.5s ease-in-out infinite;
}
.rocket-float {
  animation: mini-float 2s ease-in-out infinite;
}
.flame-flicker {
  animation: mini-flame 0.3s ease-in-out infinite alternate;
}

@keyframes mini-bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
@keyframes mini-drive {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25% { transform: translateY(-2px) rotate(-0.5deg); }
  75% { transform: translateY(-1px) rotate(0.5deg); }
}
@keyframes mini-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}
@keyframes mini-flame {
  0% { opacity: 0.6; transform: scaleY(0.9); }
  100% { opacity: 1; transform: scaleY(1.1); }
}
</style>
